// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext, type AuthContextValue } from './AuthContext';
import { useAuth } from '../hooks/useAuth';
import { useFlagsReady } from '../hooks/useFlagsReady';
import { useSiteRollout } from '../hooks/useSiteRollout';
import type { SiteRollout } from '../config/launchDarklyFlags';

/**
 * How long to hold "Loading your sites…" for LaunchDarkly's first answer
 * before showing the roster as it is. Longer than LDProvider's 5s init
 * timeout, so a slow-but-working LaunchDarkly is not cut off early.
 */
export const ROLLOUT_SETTLE_TIMEOUT_MS = 8000;

/** Stable identity for "every site is hidden", so consumers don't re-render. */
const NO_SITES: AuthContextValue['sites'] = [];

const HidEverySiteContext = createContext(false);

/**
 * True when the user has sites and the rollout hides all of them — not when
 * the organization simply has none yet. Admin Center closes on the first and
 * must stay open on the second, which is how a first site gets onboarded.
 */
export function useRolloutHidEverySite(): boolean {
  return useContext(HidEverySiteContext);
}

/**
 * Narrows the roster to the sites the `site-rollout` flag enables, for
 * everything below it. UI rollout only, not API authorization.
 *
 * It re-provides `AuthContext` rather than adding a second roster hook: the
 * site switchers, route sync, prefetchers and both shells already read
 * `useAuth().sites`, so a hidden site is simply not in the roster for any of
 * them and each keeps its existing behaviour — no enabled site is the shell's
 * own "no assigned sites" state, a link to a hidden site is a link to a site
 * outside the roster. `LaunchDarklyContext` sits above this and still sees the
 * full roster, so the targeting context never depends on the flag it feeds.
 */
export default function SiteRolloutRoster({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const ready = useFlagsReady();
  const rollout = useSiteRollout();

  // `ready` drops on every site switch while identify() re-runs, and until it
  // returns `useFlags()` may hold a value evaluated for another identity. The
  // flag is org-targeted, so keep the last value seen while ready for this
  // org and use that in between: a switch neither flashes the loading state
  // nor applies someone else's answer.
  const scope = auth.orgId ?? '';
  const [answer, setAnswer] = useState<{ scope: string; rollout: SiteRollout } | null>(null);
  const [gaveUpFor, setGaveUpFor] = useState<string | null>(null);
  const kept = answer?.scope === scope ? answer.rollout : null;
  const known = ready ? rollout : kept;
  const waiting = !!auth.meResponse && !auth.isLoading && !known && gaveUpFor !== scope;

  useEffect(() => {
    if (ready) setAnswer({ scope, rollout });
  }, [ready, scope, rollout]);

  useEffect(() => {
    if (!waiting) return undefined;
    const timer = setTimeout(() => setGaveUpFor(scope), ROLLOUT_SETTLE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [waiting, scope]);

  const value = useMemo<AuthContextValue>(() => {
    if (!auth.meResponse) return auth;
    // Hold the existing loading state, with no roster at all: the prefetchers
    // and route sync act on sites before the shell's loading return, so any
    // site offered here could be fetched or selected and then taken away.
    if (waiting) return { ...auth, sites: NO_SITES, isLoading: true };
    // Gave up waiting: whatever `useFlags()` holds was evaluated for the
    // anonymous context, not this organization, so it must not hide anything.
    if (!known || known.all) return auth;
    const sites = auth.sites.filter(site => known.sites.includes(site.site_id));
    return { ...auth, sites: sites.length > 0 ? sites : NO_SITES };
  }, [auth, waiting, known]);

  const hidEverySite = !waiting && auth.sites.length > 0 && value.sites.length === 0;

  return (
    <AuthContext.Provider value={value}>
      <HidEverySiteContext.Provider value={hidEverySite}>{children}</HidEverySiteContext.Provider>
    </AuthContext.Provider>
  );
}
